import { DateTime } from 'luxon';
import ExcelJS from 'exceljs';
import saveAs from 'file-saver';
import { HolidayDto } from '../commonTypes/organization.types';
import { LeaveRequestStatus } from '../commonTypes/leaveRequest.types';
import { LeaveRequestUserInfoIncluded } from '../commonTypes/acs/leaveRequest.types';
import { convertIsoToDiffDaysWithoutWeekendsAndHolidays } from './date/dateHelper';

export const WORKING_HOURS_PER_DAY = 8;

export interface AnnualLeaveReportRow {
    name: string;
    dateRange: string;
    days: number;
    hours: number;
    comment: string;
}

export const formatLeaveDateRangeShort = (fromDateIso: string, toDateIso: string): string => {
    const from = DateTime.fromISO(fromDateIso);
    const to = DateTime.fromISO(toDateIso);
    const formatDay = (date: DateTime) => `${date.day}. ${date.month}.`;
    return `${formatDay(from)} - ${formatDay(to)}`;
};

export const buildAnnualLeaveReportRows = (
    requests: LeaveRequestUserInfoIncluded[],
    holidays: HolidayDto[],
    selectedMonth: DateTime,
    annualLeavePolicyType: string
): AnnualLeaveReportRow[] => {
    const monthStart = selectedMonth.startOf('month');
    const monthEnd = selectedMonth.endOf('month');

    return requests
        .filter(request => request.status === LeaveRequestStatus.APPROVED)
        .filter(request => request.policyType === annualLeavePolicyType)
        .filter(request => {
            const fromDate = DateTime.fromISO(request.fromDate);
            const toDate = DateTime.fromISO(request.toDate);
            return fromDate <= monthEnd && toDate >= monthStart;
        })
        .map(request => {
            const fromDate = DateTime.fromISO(request.fromDate);
            const toDate = DateTime.fromISO(request.toDate);
            const clippedFromDate = fromDate < monthStart ? monthStart : fromDate;
            const clippedToDate = toDate > monthEnd ? monthEnd : toDate;
            const days = convertIsoToDiffDaysWithoutWeekendsAndHolidays(
                clippedFromDate.toISODate() as string,
                clippedToDate.toISODate() as string,
                holidays,
                request.leaveDurationType
            );
            return {
                name: [request.givenName, request.familyName].filter(Boolean).join(' ') || request.email,
                dateRange: formatLeaveDateRangeShort(request.fromDate, request.toDate),
                days,
                hours: days * WORKING_HOURS_PER_DAY,
                comment: request.description ?? '',
            };
        });
};

export const buildAnnualLeaveReportFilename = (selectedMonth: DateTime, extension: 'xlsx' | 'pdf'): string =>
    `absence-vacation-${selectedMonth.toFormat('LLL').toLowerCase()}-${selectedMonth.year}.${extension}`;

const EXCEL_WHITE_BORDER: Partial<ExcelJS.Borders> = {
    top: { style: 'thin', color: { argb: 'FFFFFFFF' } },
    bottom: { style: 'thin', color: { argb: 'FFFFFFFF' } },
    left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
    right: { style: 'thin', color: { argb: 'FFFFFFFF' } },
};

const EXCEL_HEADER_FILL: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4B99F5' } };
const EXCEL_HEADER_FONT: Partial<ExcelJS.Font> = { name: 'Arial', bold: true, color: { argb: 'FFFFFFFF' } };
const EXCEL_ROW_FILLS: [ExcelJS.Fill, ExcelJS.Fill] = [
    { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } },
    { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEDF5FF' } },
];
const EXCEL_ROW_FONT: Partial<ExcelJS.Font> = { name: 'Arial', color: { argb: 'FF22262F' } };

export interface ReportColumn<T> {
    header: string;
    width: number;
    excelWidth?: number;
    value: (row: T) => string | number;
}

const addStyledSheet = <T>(workbook: ExcelJS.Workbook, sheetName: string, columns: ReportColumn<T>[], rows: T[]): void => {
    const sheet = workbook.addWorksheet(sheetName, { views: [{ state: 'frozen', ySplit: 1 }] });

    sheet.columns = columns.map((column, index) => ({ header: column.header, key: `col${index}`, width: column.excelWidth ?? column.width }));

    const headerRow = sheet.getRow(1);
    headerRow.height = 18;
    headerRow.eachCell(cell => {
        cell.font = EXCEL_HEADER_FONT;
        cell.fill = EXCEL_HEADER_FILL;
        cell.border = EXCEL_WHITE_BORDER;
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    });

    rows.forEach((row, rowIndex) => {
        const excelRow = sheet.addRow(columns.map(column => column.value(row)));
        excelRow.height = 16;
        excelRow.eachCell(cell => {
            cell.font = EXCEL_ROW_FONT;
            cell.fill = EXCEL_ROW_FILLS[rowIndex % 2];
            cell.border = EXCEL_WHITE_BORDER;
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        });
    });

    if (rows.length > 0) {
        sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: columns.length } };
    }
};

const saveWorkbook = async (workbook: ExcelJS.Workbook, filename: string): Promise<void> => {
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), filename);
};

export const downloadStyledExcel = async <T>(sheetName: string, columns: ReportColumn<T>[], rows: T[], filename: string): Promise<void> => {
    const workbook = new ExcelJS.Workbook();
    addStyledSheet(workbook, sheetName, columns, rows);
    await saveWorkbook(workbook, filename);
};

export interface ExcelSheetConfig<T> {
    sheetName: string;
    columns: ReportColumn<T>[];
    rows: T[];
}

export const downloadMultiSheetExcel = async (sheets: ExcelSheetConfig<any>[], filename: string): Promise<void> => {
    const workbook = new ExcelJS.Workbook();
    sheets.forEach(({ sheetName, columns, rows }) => addStyledSheet(workbook, sheetName, columns, rows));
    await saveWorkbook(workbook, filename);
};

export interface ReportPage<T> {
    content: T[];
    totalElements: number;
}

const REPORT_EXPORT_PAGE_SIZE = 100000;

export const fetchAllReportRows = <T>(fetchPage: (page: number, size: number) => Promise<ReportPage<T>>): Promise<T[]> =>
    fetchPage(0, REPORT_EXPORT_PAGE_SIZE).then(({ content, totalElements }) => {
        if (content.length >= totalElements) return content;
        return fetchPage(0, totalElements).then(page => page.content);
    });

export const slugify = (value: string): string => value.trim().toLowerCase().replace(/\s+/g, '-');

export interface ExportFilterDescriptor {
    label: string;
    value?: string;
}

export const buildFilterSuffix = (filters: ExportFilterDescriptor[]): string => {
    const active = filters.filter((filter): filter is Required<ExportFilterDescriptor> => Boolean(filter.value));
    if (active.length === 0) return '';
    return `-${active.map(filter => slugify(`${filter.label}-${filter.value}`)).join('-')}`;
};

export const buildLeaveBalanceExportFilename = (policyLabel: string, filters: ExportFilterDescriptor[], extension: 'xlsx' | 'pdf'): string =>
    `absence-leave-balance-${slugify(policyLabel)}${buildFilterSuffix(filters)}.${extension}`;

export const buildTopEmployeesExportFilename = (policyLabel: string, filters: ExportFilterDescriptor[], extension: 'xlsx' | 'pdf'): string =>
    `absence-top-employees-${slugify(policyLabel)}${buildFilterSuffix(filters)}.${extension}`;

export const buildUpcomingAbsencesExportFilename = (selectedMonth: DateTime, filters: ExportFilterDescriptor[], extension: 'xlsx' | 'pdf'): string =>
    `absence-upcoming-${selectedMonth.toFormat('LLL').toLowerCase()}-${selectedMonth.year}${buildFilterSuffix(filters)}.${extension}`;

export const buildWeeklyTrendExportFilename = (weekStart: DateTime, filters: ExportFilterDescriptor[], extension: 'xlsx' | 'pdf'): string =>
    `absence-weekly-trend-${weekStart.toFormat('yyyy-LL-dd')}${buildFilterSuffix(filters)}.${extension}`;

export const buildQuarterlyTrendExportFilename = (year: number, filters: ExportFilterDescriptor[], extension: 'xlsx' | 'pdf'): string =>
    `absence-quarterly-trend-${year}${buildFilterSuffix(filters)}.${extension}`;

export const buildMonthlyDetailExportFilename = (month: DateTime, filters: ExportFilterDescriptor[], extension: 'xlsx' | 'pdf'): string =>
    `absence-monthly-breakdown-${month.toFormat('LLL').toLowerCase()}-${month.year}${buildFilterSuffix(filters)}.${extension}`;

export const buildPolicyFilterDescriptor = (leaveTypes: string[], hasActiveFilter: boolean): ExportFilterDescriptor => ({
    label: 'policy',
    value: hasActiveFilter ? leaveTypes.join('-') : undefined,
});

export interface LeaveTypeSeries {
    leaveType: string;
    colour: string;
}

export const buildSeriesColumns = <T>(
    leadColumn: ReportColumn<T>,
    series: LeaveTypeSeries[],
    getValue: (row: T, leaveType: string) => number,
    totalLabel: string
): ReportColumn<T>[] => [
    leadColumn,
    ...series.map(({ leaveType }) => ({
        header: leaveType,
        width: 90,
        excelWidth: 18,
        value: (row: T) => getValue(row, leaveType),
    })),
    {
        header: totalLabel,
        width: 70,
        excelWidth: 14,
        value: (row: T) => series.reduce((sum, { leaveType }) => sum + getValue(row, leaveType), 0),
    },
];
